import { Component, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { classifyError } from '@/lib/errorCodes';

interface Props { children: ReactNode }
interface State { error: Error | null }

/**
 * Captura erros de renderização e mostra uma tela de recuperação em vez de
 * deixar a aplicação inteira branca.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error) {
    console.error('ErrorBoundary capturou:', error);
  }

  render() {
    if (this.state.error) {
      const { code } = classifyError(this.state.error);
      return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-surface">
          <div className="card p-8 max-w-md w-full text-center space-y-4">
            <div className="mx-auto w-12 h-12 rounded-full bg-negative/10 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-negative" />
            </div>
            <h2 className="font-display font-bold text-lg text-ink">Desculpe, tivemos um erro</h2>
            <p className="text-sm text-ink-muted">
              Tente novamente. Se o problema continuar, informe este código ao suporte:
            </p>
            <p className="font-mono text-sm font-bold text-ink bg-surface-subtle rounded-lg py-2 px-3 inline-block">
              {code}
            </p>
            <details className="text-left">
              <summary className="text-xs text-ink-soft cursor-pointer">Detalhe técnico</summary>
              <p className="text-xs text-ink-muted break-words mt-1">{this.state.error.message}</p>
            </details>
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => { this.setState({ error: null }); window.location.href = '/'; }}
                className="btn-primary"
              >
                Voltar ao início
              </button>
              <button onClick={() => window.location.reload()} className="btn-secondary">
                Recarregar
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
